/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faArrowRight,
    faBoxesStacked,
    faCircleCheck,
    faCircleQuestion,
    faFlagCheckered,
    faFolderOpen,
    faLinkSlash,
    faOctagonExclamation,
    faPause,
    faSeedling,
    faSignalSlash,
    faSnowflake,
    faSpinner,
    faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Progress } from "reactstrap";

import useConfig from "../../../../actions/Tenants/config/configHook";
import { useAllocatedLayers } from "../../../../hooks/useFacilityLocationQueries";
import useRipeningRoom, { DATE_URL_FORMAT } from "../hooks/useRipeningRoom";
import { formatRoomTime, getLowOxygen, getRoomAvailability, RoomAvailabilityColor, RoomAvailabilityKind } from "./roomAvailability";
import { getRunPhaseName, getRunPhaseProgress, getRunProgramName, getRunProgress, useRipeningRoomLiveRun } from "./roomRun";

const ICONS: Record<RoomAvailabilityKind, IconDefinition> = {
    checking: faSpinner,
    free: faCircleCheck,
    pallets_inside: faBoxesStacked,
    cycle_open: faFolderOpen,
    ripening: faSeedling,
    storage: faSnowflake,
    paused: faPause,
    no_cycle: faTriangleExclamation,
    done: faFlagCheckered,
    emergency_stop: faOctagonExclamation,
    no_connection: faSignalSlash,
    no_update: faSignalSlash,
    no_status: faCircleQuestion,
    unavailable: faCircleQuestion,
    unlinked: faLinkSlash,
};

// * Bootstrap has no purple theme colour: build its subtle/emphasis look from --bs-purple.
const purpleCss = css`
    background-color: color-mix(in srgb, var(--bs-purple, #6f42c1) 12%, var(--bs-body-bg, #fff));
    border-color: color-mix(in srgb, var(--bs-purple, #6f42c1) 35%, var(--bs-body-bg, #fff)) !important;
    .room-status-label { color: color-mix(in srgb, var(--bs-purple, #6f42c1) 80%, var(--bs-body-color, #000)); }
`;

const cardClass = (color: RoomAvailabilityColor) => (color === "purple"
    ? "rounded-3 border p-3"
    : `rounded-3 border border-${color}-subtle bg-${color}-subtle p-3`);

const labelClass = (color: RoomAvailabilityColor) => (color === "purple" ? "" : `text-${color}-emphasis`);

const progressCss = css`
    height: 0.6rem;
    flex: 1;
`;

/** Pallets in the room today, whatever date range the page shows; null while unknown. */
function useTodayPalletCount(locationId: string | undefined): number | null {
    const { layers } = useRipeningRoom();
    const today = dayjs().format(DATE_URL_FORMAT);
    const { data, isError, refetch } = useAllocatedLayers(locationId, today, today);
    // * Pallets moved in or out on the page refresh the page's own range; follow along.
    const seenLayers = useRef(layers);
    useEffect(() => {
        if (seenLayers.current === layers) return;
        seenLayers.current = layers;
        if (locationId) refetch();
    }, [layers, locationId, refetch]);
    if (isError || !data) return null;
    const day = (data.days || []).find((d: any) => dayjs(d.date).format(DATE_URL_FORMAT) === today);
    return new Set(day?.layer_ids || []).size;
}

/** Always-visible room status at the top of the room sidebar: what the room is doing
 *  and, when it is idle, whether it is free to use. */
export const RipeningRoomStatusCard: React.FC = () => {
    const { location_id } = useParams();
    const { currentLocation, locations, activeRipeningCycle } = useRipeningRoom();
    const config = useConfig();
    // * Without RipeWise every room counts as unlinked: no controller status is fetched or shown.
    const ripewiseEnabled = !!config.ripewise_config?.enabled;
    const gatewayId = ripewiseEnabled ? currentLocation?.gateway : undefined;
    const roomId = ripewiseEnabled ? currentLocation?.controller_room : undefined;
    const { data, isError, run } = useRipeningRoomLiveRun(gatewayId, roomId, currentLocation?.tenant_id);
    const palletCount = useTodayPalletCount(location_id);
    const clientHasLinkedRooms = useMemo(
        () => ripewiseEnabled && (locations || []).some((location) => !!location?.gateway && !!location?.controller_room),
        [locations, ripewiseEnabled],
    );

    if (!currentLocation) return null;
    const availability = getRoomAvailability({
        linked: !!gatewayId && !!roomId,
        clientHasLinkedRooms,
        status: data,
        run,
        statusError: isError,
        palletCount,
        hasActiveCycle: !!activeRipeningCycle,
    });
    if (!availability) return null;

    const { kind, label, detail, next, color } = availability;
    const lowOxygen = getLowOxygen(run, activeRipeningCycle?.meta);
    const showRun = ["ripening", "storage", "paused", "no_cycle"].includes(kind);
    const program = showRun ? getRunProgramName(run) : null;
    // * A store has no phases and no end: it holds until it is stopped.
    const phase = showRun && kind !== "storage" ? getRunPhaseName(run) : null;
    // * The bar is the whole program; how far the current phase is goes next to its name.
    const progress = showRun && kind !== "storage" ? getRunProgress(run) : null;
    const phaseProgress = phase ? getRunPhaseProgress(run) : null;
    const readyAt = kind === "ripening" ? formatRoomTime(run?.end_at) : null;
    // * When the room last reported, so a user can tell how current the card is.
    const lastUpdate = kind === "no_connection" || kind === "no_update" ? null : formatRoomTime(data?.last_raw_at);

    return (
        <div
            className={cardClass(color)}
            css={color === "purple" ? purpleCss : undefined}
            data-testid="ripening-room-status-card"
            data-status={kind}
            role="status"
        >
            <div className="small text-muted fw-semibold text-uppercase mb-1">Room status</div>
            <div className={`room-status-label d-flex align-items-center gap-2 fs-5 fw-bold ${labelClass(color)}`}>
                <FontAwesomeIcon icon={ICONS[kind]} spin={kind === "checking"} fixedWidth />
                <span>{label}</span>
            </div>
            {detail && <div className="small mt-1">{detail}</div>}
            {lowOxygen && (
                <div className="rounded-2 bg-danger text-white p-2 mt-2" data-testid="low-oxygen-alert" role="alert">
                    <div className="fw-bold d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faTriangleExclamation} fixedWidth />
                        Low oxygen program running
                    </div>
                    <div className="small fw-semibold">Do not enter the room.</div>
                    {lowOxygen.lampOn && <div className="small">Blue low-oxygen lamp is on at the door.</div>}
                </div>
            )}
            {program && <div className="fw-semibold mt-2">{program}</div>}
            {phase && (
                <div className="small" data-testid="room-status-phase">
                    {phase}
                    {phaseProgress !== null && <span className="text-muted"> · {phaseProgress}% of phase</span>}
                </div>
            )}
            {(progress !== null || readyAt) && (
                <div className="d-flex align-items-center gap-2 mt-2 small">
                    {progress !== null && (
                        <>
                            <Progress value={progress} color="success" css={progressCss} aria-label={`Program progress: ${progress}%`} />
                            <span className="fw-semibold text-success">{progress}%</span>
                        </>
                    )}
                    {readyAt && <span className="text-muted text-nowrap">ready {readyAt}</span>}
                </div>
            )}
            {lastUpdate && <div className="small text-muted mt-2" data-testid="room-status-last-update">Last update {lastUpdate}</div>}
            {next && (
                <div className="small fw-semibold mt-2">
                    <FontAwesomeIcon icon={faArrowRight} className="me-1" />
                    Next: {next}
                </div>
            )}
        </div>
    );
};

export default RipeningRoomStatusCard;
